import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq } from 'ember-truth-helpers';
import type Owner from '@ember/owner';
import type { Store } from '@warp-drive/core';
import type Notifications from '../services/notifications';
import type {
  UserSource,
  UserSourceContentSourceEmbed,
} from '../schemas/user-source.ts';
import type { PaginationMeta } from '../utils/api-types.ts';
import { getUserSources, updateUserSourceDismissed } from '../builders/profile';
import SourceCard from './source-card';
import InfiniteScrollTrigger from './infinite-scroll-trigger.gts';
import Icon from './icon';
import tw from '../helpers/tw';
import pullToRefresh from '../modifiers/pull-to-refresh.ts';
import responsiveColumns from '../modifiers/responsive-columns.ts';
import YoutubeIcon from '~icons/simple-icons/youtube';
import TiktokIcon from '~icons/simple-icons/tiktok';
import InstagramIcon from '~icons/simple-icons/instagram';
import PodcastIcon from '~icons/lucide/podcast';
import ImageIcon from '~icons/lucide/image';

interface MySourcesListSignature {
  Element: HTMLDivElement;
}

/** Safely extract the embedded contentSource object */
function cs(userSource: UserSource): UserSourceContentSourceEmbed | null {
  return userSource.contentSource;
}

const PLATFORMS = [
  { key: '', label: 'All', icon: undefined },
  { key: 'youtube', label: 'YouTube', icon: YoutubeIcon },
  { key: 'tiktok', label: 'TikTok', icon: TiktokIcon },
  { key: 'instagram', label: 'Instagram', icon: InstagramIcon },
  { key: 'podcast', label: 'Podcast', icon: PodcastIcon },
  { key: 'image', label: 'Image', icon: ImageIcon },
] as const;

const ITEM_TYPES = [
  { key: '', label: 'All types' },
  { key: 'movie', label: 'Movies' },
  { key: 'tv_show', label: 'TV Shows' },
  { key: 'book', label: 'Books' },
  { key: 'music', label: 'Music' },
  { key: 'video_game', label: 'Video Games' },
  { key: 'podcast', label: 'Podcasts' },
  { key: 'recipe', label: 'Recipes' },
  { key: 'place', label: 'Places' },
  { key: 'product', label: 'Products' },
];

export default class MySourcesList extends Component<MySourcesListSignature> {
  @service declare store: Store;
  @service declare notifications: Notifications;

  @tracked sources: UserSource[] = [];
  @tracked meta: PaginationMeta = { page: 1, totalPages: 1 };
  @tracked isLoading = true;
  @tracked isLoadingMore = false;
  @tracked showDismissed = false;
  @tracked activePlatform = '';
  @tracked activeItemType = '';
  @tracked actionInProgress = '';
  @tracked numColumns = window.matchMedia('(min-width: 1024px)').matches
    ? 3
    : 2;

  @action
  setNumColumns(n: number) {
    if (n !== this.numColumns) this.numColumns = n;
  }

  get columnedSources(): UserSource[][] {
    const cols: UserSource[][] = Array.from(
      { length: this.numColumns },
      () => []
    );
    for (let i = 0; i < this.sources.length; i++) {
      cols[i % this.numColumns]!.push(this.sources[i]!);
    }
    return cols;
  }

  constructor(owner: Owner, args: object) {
    super(owner, args);
    void this.loadData();
  }

  get hasMore() {
    return this.meta.page < this.meta.totalPages;
  }

  get platforms() {
    return PLATFORMS;
  }

  get itemTypes() {
    return ITEM_TYPES;
  }

  @action
  async loadData() {
    this.isLoading = true;
    try {
      const result = await this.store.request({
        ...getUserSources({
          dismissed: this.showDismissed ? 'true' : undefined,
          platform: this.activePlatform || undefined,
          itemType: this.activeItemType || undefined,
        }),
        // Personal list that changes right after submitting a URL — always
        // refetch instead of serving the WarpDrive-cached document.
        cacheOptions: { reload: true },
      });

      const json = result.content as {
        data?: UserSource[];
        meta?: PaginationMeta;
      };

      this.sources = json.data ?? [];
      this.meta = json.meta ?? { page: 1, totalPages: 1 };
    } catch {
      this.notifications.error('Failed to load sources');
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async loadMore() {
    if (this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    try {
      const nextPage = this.meta.page + 1;
      const result = await this.store.request({
        ...getUserSources({
          page: nextPage.toString(),
          dismissed: this.showDismissed ? 'true' : undefined,
          platform: this.activePlatform || undefined,
          itemType: this.activeItemType || undefined,
        }),
        cacheOptions: { reload: true },
      });

      const json = result.content as {
        data?: UserSource[];
        meta?: PaginationMeta;
      };

      this.sources = [...this.sources, ...(json.data ?? [])];
      this.meta = json.meta ?? this.meta;
    } finally {
      this.isLoadingMore = false;
    }
  }

  @action
  toggleDismissed() {
    this.showDismissed = !this.showDismissed;
    void this.loadData();
  }

  @action
  filterByPlatform(platform: string) {
    this.activePlatform = platform;
    void this.loadData();
  }

  @action
  changeItemType(event: Event) {
    this.activeItemType = (event.target as HTMLSelectElement).value;
    void this.loadData();
  }

  @action
  async dismissSource(userSource: UserSource) {
    this.actionInProgress = userSource.id ?? '';
    try {
      await this.store.request(updateUserSourceDismissed(userSource.id!, true));
      this.notifications.success('Source dismissed (items also dismissed)');
      void this.loadData();
    } catch {
      this.notifications.error('Failed to dismiss source');
    } finally {
      this.actionInProgress = '';
    }
  }

  @action
  async restoreSource(userSource: UserSource) {
    this.actionInProgress = userSource.id ?? '';
    try {
      await this.store.request(
        updateUserSourceDismissed(userSource.id!, false)
      );
      this.notifications.success('Source restored (items also restored)');
      void this.loadData();
    } catch {
      this.notifications.error('Failed to restore source');
    } finally {
      this.actionInProgress = '';
    }
  }

  <template>
    <div ...attributes {{pullToRefresh this.loadData}}>
      {{! Filter bar }}
      <div class="mb-4 flex flex-col gap-3">
        {{! Platform pills }}
        <div class="flex flex-wrap gap-1.5">
          {{#each this.platforms as |p|}}
            <button
              type="button"
              class={{tw
                "btn btn-xs gap-1"
                (if (eq this.activePlatform p.key) "btn-primary" "btn-ghost")
              }}
              {{on "click" (fn this.filterByPlatform p.key)}}
            >
              {{#if p.icon}}
                <Icon @icon={{p.icon}} class="h-3 w-3" />
              {{/if}}
              {{p.label}}
            </button>
          {{/each}}
        </div>

        {{! Item type + dismissed row }}
        <div class="flex flex-wrap items-center gap-2">
          <select
            class="select select-bordered select-sm"
            aria-label="Filter by item type"
            {{on "change" this.changeItemType}}
          >
            {{#each this.itemTypes as |t|}}
              <option value={{t.key}} selected={{eq this.activeItemType t.key}}>
                {{t.label}}
              </option>
            {{/each}}
          </select>

          <label class="label cursor-pointer gap-2">
            <input
              type="checkbox"
              class="toggle toggle-sm"
              checked={{this.showDismissed}}
              {{on "change" this.toggleDismissed}}
            />
            <span class="label-text">Show dismissed</span>
          </label>
        </div>
      </div>

      {{#if this.isLoading}}
        <div class="flex justify-center py-12">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      {{else if this.sources.length}}
        <div
          class="flex gap-4"
          {{responsiveColumns onChange=this.setNumColumns}}
        >
          {{#each this.columnedSources as |col|}}
            <div class="flex min-w-0 flex-1 flex-col gap-4">
              {{#each col as |userSource|}}
                {{#let (cs userSource) as |source|}}
                  {{#if source}}
                    <SourceCard
                      @source={{source}}
                      @dismissed={{userSource.dismissed}}
                      @onDismiss={{fn this.dismissSource userSource}}
                      @onRestore={{fn this.restoreSource userSource}}
                      @disabled={{eq this.actionInProgress userSource.id}}
                    />
                  {{/if}}
                {{/let}}
              {{/each}}
            </div>
          {{/each}}
        </div>

        <InfiniteScrollTrigger
          @hasMore={{this.hasMore}}
          @isLoading={{this.isLoadingMore}}
          @onLoadMore={{this.loadMore}}
        />
      {{else}}
        <div class="text-base-content/60 py-12 text-center">
          {{#if this.showDismissed}}
            No dismissed sources.
          {{else if this.activePlatform}}
            No sources for this platform.
          {{else if this.activeItemType}}
            No sources with this type of content.
          {{else}}
            No sources yet. Submit content to start your collection.
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
